import React from 'react';
import { Box, Container, Typography, Link, IconButton } from '@mui/material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  Github, 
  Twitter, 
  Linkedin, 
  Youtube, 
  Instagram, 
  Mail, 
  Phone, 
  MapPin,
  ArrowUp
} from 'lucide-react';

const Footer: React.FC = () => {
  const navigate = useNavigate();
  
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };


  const socialLinks = [
    { icon: <Github size={20} />, href: '#', label: 'GitHub' },
    { icon: <Twitter size={20} />, href: '#', label: 'Twitter' },
    { icon: <Linkedin size={20} />, href: '#', label: 'LinkedIn' },
    { icon: <Youtube size={20} />, href: '#', label: 'YouTube' },
    { icon: <Instagram size={20} />, href: '#', label: 'Instagram' },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: 'easeOut' as any,
      },
    },
  };

  return (
    <Box
      sx={{
        bgcolor: 'rgba(30, 41, 59, 0.92)',
        backdropFilter: 'saturate(1.1)',
        color: '#F8FAFC',
        borderTop: '1px solid #E5E7EB',
        position: 'relative',
        overflow: 'hidden',
        '& .MuiTypography-root': { color: 'inherit' },
        '& a': { color: '#93C5FD', '&:hover': { color: '#BFDBFE' } },
      }}
    >

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2, py: 3 }}>
            {/* Brand Section */}
            <Box>
              <motion.div variants={itemVariants}>
                <Link component="button" onClick={() => navigate('/')} sx={{ p: 0, display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }}>
                  <Box
                    component="img"
                    src="/cropped-revinlogoyoutube-2.png"
                    alt="Reviving India"
                    sx={{ 
                      height: 44,
                      width: 'auto',
                      objectFit: 'contain',
                      mb: 1,
                      cursor: 'pointer',
                    }}
                  />
                </Link>
                <Typography
                  variant="body1"
                  sx={{
                    color: 'text.secondary',
                    mb: 1.5,
                    lineHeight: 1.5,
                    fontSize: '0.9rem',
                  }}
                >
                  Empowering the next generation of technologists through comprehensive
                  education in Python Robotics, AI, Machine Learning, and Data Science.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                  {socialLinks.map((social, index) => (
                    <motion.div
                      key={social.label}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <IconButton
                        href={social.href}
                        sx={{
                          color: '#94A3B8',
                          bgcolor: 'rgba(37, 99, 235, 0.15)',
                          '&:hover': {
                            color: '#2563EB',
                            bgcolor: 'rgba(37, 99, 235, 0.25)',
                            transform: 'translateY(-2px)',
                          },
                        }}
                      >
                        {social.icon}
                      </IconButton>
                    </motion.div>
                  ))}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Mail size={14} color="#94A3B8" />
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                    revivingindiaclub@gmail.com
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Phone size={14} color="#94A3B8" />
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                    +91 9716817710 / 6204716356
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MapPin size={14} color="#94A3B8" />
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                    Meena Bazar, Near Patna Nagar Nigam, 800007
                  </Typography>
                </Box>
              </motion.div>
            </Box>

          </Box>

          {/* Bottom Section */}
          <motion.div variants={itemVariants}>
            <Box
              sx={{
                borderTop: '1px solid rgba(51, 65, 85, 0.6)',
                py: 1.5,
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  textAlign: { xs: 'center', md: 'left' },
                  fontSize: '0.8rem',
                }}
              >
                © 2024 Reviving India. All rights reserved. Made with ❤️ in India.
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                  Follow us for updates
                </Typography>
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <IconButton
                    onClick={scrollToTop}
                    sx={{
                      color: 'primary.main',
                      bgcolor: 'rgba(99, 102, 241, 0.1)',
                      '&:hover': {
                        bgcolor: 'rgba(51, 65, 85, 0.6)',
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    <ArrowUp size={20} />
                  </IconButton>
                </motion.div>
              </Box>
            </Box>
          </motion.div>
        </motion.div>
      </Container>
    </Box>
  );
};

export default Footer;
